// src/features/inventory/pages/BatchDetailPage/tabs/index.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  History as HistoryIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import BatchInfo from './BatchInfo';
import StockLog from './StockLog';
import { useInventoryStockPermissions } from '../../../../auth/hooks/usePermissions';

function TabPanel({ children, value, index, sx }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`batch-tabpanel-${index}`}
      aria-labelledby={`batch-tab-${index}`}
      sx={value !== index ? null : { ...sx, minHeight: 0 }}
    >
      {value === index && <Box sx={{ minHeight: 0, ...sx }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `batch-tab-${index}`,
    'aria-controls': `batch-tabpanel-${index}`,
  };
}

export default function BatchTabs({ lotId, lot, canRecord, onLotUpdated }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canRead: canReadStock } = useInventoryStockPermissions();

  const tabs = [
    {
      key: 'info',
      label: t('inventory:batches.tabs.info.title'),
      description: t('inventory:batches.tabs.info.description'),
      icon: InfoIcon,
      component: BatchInfo,
      props: { lot, lotId, canRecord, onLotUpdated },
    },
    ...(canReadStock ? [{
      key: 'stock_log',
      label: t('inventory:batches.tabs.stock_log.title'),
      description: t('inventory:batches.tabs.stock_log.description'),
      icon: HistoryIcon,
      component: StockLog,
      props: { lotId },
    }] : []),
  ];

  const getTabFromParams = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const idx = tabs.findIndex((tab) => tab.key === tabParam);
      return idx !== -1 ? idx : 0;
    }
    return 0;
  };

  const [currentTab, setCurrentTab] = useState(getTabFromParams);

  useEffect(() => {
    setCurrentTab(getTabFromParams());
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabs[newValue].key);
    setSearchParams(newParams);
  };

  return (
    <Box sx={{ width: '100%', mt: 3, flex: 1, minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          height: '100%',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="batch detail tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: isMobile ? 56 : 64,
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 1.5 : 3,
              '&.Mui-selected': { fontWeight: 600, bgcolor: 'background.paper' },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.key}
                icon={<Icon sx={{ fontSize: isMobile ? 34 : 36 }} />}
                iconPosition="start"
                label={
                  <Box sx={{ textAlign: 'left', ml: isMobile ? 0.5 : 1, pb: 1 }}>
                    <Typography variant="body2" fontWeight="inherit">{tab.label}</Typography>
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">{tab.description}</Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'center', justifyContent: 'flex-start', minWidth: 'unset', height: '100%' }}
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>

        <Box sx={{ bgcolor: 'background.paper', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {tabs.map((tab, index) => {
            const TabComponent = tab.component;
            return (
              <TabPanel
                key={tab.key}
                value={currentTab}
                index={index}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <TabComponent {...tab.props} />
              </TabPanel>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
